import { router, usePage } from "@inertiajs/react"
import { Loader2, Mail, Plus, RotateCcw, Trash2, X } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { toast } from "sonner"

import EmailPreview, {
  type EmailPreviewData,
  requestEmailPreview,
} from "@/components/shared/email-preview"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import { csrfFetch } from "@/lib/csrf-fetch"
import {
  emailPreviewLegacyOrgReportPath,
  sendLegacyOrgReportPath,
} from "@/routes"
import type { SavedReportRecipientData, SharedProps } from "@/types"

import { FORMAT_OPTIONS, type Format } from "./schedule-options"

interface OrgUser {
  id: number
  name: string
  email: string
}

interface Recipient {
  user_id?: number
  email: string
  label: string
}

interface SavedRecipientMutationResponse {
  ok?: boolean
  saved_report_recipient?: SavedReportRecipientData
  saved_recipient?: SavedReportRecipientData
  recipient?: SavedReportRecipientData
  error?: string
}

type SendRecipient =
  | { saved_recipient_id: number }
  | { user_id?: number; email: string }

interface Props {
  reportKey: string
  reportTitle: string
  orgSlug: string
  orgUsers: OrgUser[]
  /** Organization-wide reusable addresses for manual sends. */
  savedRecipients?: SavedReportRecipientData[]
  /** Editors/admins may explicitly re-enable an unavailable saved row. */
  canReenableSavedRecipients?: boolean
  /** Explicit org-membership gate for persistent-list mutations. */
  canManageList?: boolean
  // Optional period — billing passes year + month so the audit log + subject
  // line carry the period; non-billing reports omit them.
  billingYear?: number
  billingMonth?: number
  // Button visual variant — billing page uses "default" (primary), index
  // cards use "outline" to sit alongside the other download buttons.
  triggerVariant?: "default" | "outline" | "secondary"
  triggerLabel?: string
  // Controlled mode: when `open` + `onOpenChange` are provided, the
  // dialog is driven by the parent (e.g. from a dropdown menu item)
  // and the built-in trigger button is hidden. Form state still
  // resets on close.
  open?: boolean
  onOpenChange?: (next: boolean) => void
  // The on-screen scope the report is viewed under (facility/customer/grouping/
  // visible columns). Merged into the POST body so the emailed attachments
  // match what's on screen. Omit (e.g. from the index cards) to send the
  // full default report. `scopeSummary` is a human-readable one-liner shown
  // in the dialog so the operator sees exactly what will be emailed.
  scopeParams?: Record<string, string | number | string[] | undefined>
  scopeSummary?: string
}

const NO_SAVED_RECIPIENTS: SavedReportRecipientData[] = []

function defaultSubject(title: string, year?: number, month?: number): string {
  if (year && month) {
    const d = new Date(year, month - 1, 1)
    const label = d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
    })
    return `${title} — ${label}`
  }
  const today = new Date().toISOString().slice(0, 10)
  return `${title} — ${today}`
}

function recipientKey(email: string | null | undefined): string {
  return email?.trim().toLowerCase() ?? ""
}

function initiallySelected(
  recipients: SavedReportRecipientData[],
): Set<number> {
  return new Set(
    recipients
      .filter((sub) => sub.enabled && sub.recipient_email)
      .map((sub) => sub.id),
  )
}

function upsertSavedRecipient(
  current: SavedReportRecipientData[],
  next: SavedReportRecipientData,
): SavedReportRecipientData[] {
  const index = current.findIndex((sub) => sub.id === next.id)
  if (index < 0) return [...current, next]
  return current.map((sub) => (sub.id === next.id ? next : sub))
}

export default function SendReportDialog({
  reportKey,
  reportTitle,
  orgSlug,
  orgUsers,
  savedRecipients = NO_SAVED_RECIPIENTS,
  canReenableSavedRecipients = false,
  canManageList = false,
  billingYear,
  billingMonth,
  triggerVariant = "outline",
  triggerLabel = "Send",
  open: controlledOpen,
  onOpenChange: controlledOnChange,
  scopeParams,
  scopeSummary,
}: Props) {
  const currentUser = usePage<SharedProps>().props.auth.user
  const routes = useOrgRoutes()
  const controlled = controlledOpen !== undefined
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const open = controlled ? (controlledOpen as boolean) : uncontrolledOpen
  const dialogOpenRef = useRef(open)
  const setOpen = (next: boolean) => {
    if (controlled) controlledOnChange?.(next)
    else setUncontrolledOpen(next)
  }

  // Keep a local copy so adding a saved recipient is immediately visible even
  // when the operator closes and reopens without sending/reloading the page.
  const initialSavedRecipients = savedRecipients
  const [savedRecipientRows, setSavedRecipientRows] = useState<
    SavedReportRecipientData[]
  >(initialSavedRecipients)
  const [selectedSavedRecipientIds, setSelectedSavedRecipientIds] = useState<
    Set<number>
  >(() => initiallySelected(initialSavedRecipients))
  const [recipients, setRecipients] = useState<Recipient[]>([])
  const [pickedUserId, setPickedUserId] = useState<string>("")
  const [externalEmail, setExternalEmail] = useState("")
  const [saveForFuture, setSaveForFuture] = useState(true)
  const [subject, setSubject] = useState(
    defaultSubject(reportTitle, billingYear, billingMonth),
  )
  const [message, setMessage] = useState("")
  // PDF + CSV pre-checked, XLSX off — matches the plan default.
  const [formats, setFormats] = useState<Record<Format, boolean>>({
    pdf: true,
    csv: true,
    xlsx: false,
  })
  const [mutatingList, setMutatingList] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const recipientMutationRef = useRef(false)
  const recipientControlsLocked = mutatingList || submitting
  const [ccMe, setCcMe] = useState(true)

  // An Inertia reload remains the source of truth for the saved-recipient list.
  // source of truth. Local JSON mutations also update this state immediately.
  useEffect(() => {
    const next = savedRecipients
    setSavedRecipientRows(next)
    setSelectedSavedRecipientIds(initiallySelected(next))
  }, [savedRecipients])

  useEffect(() => {
    dialogOpenRef.current = open
  }, [open])

  const resetComposition = () => {
    recipientMutationRef.current = false
    setRecipients([])
    setPickedUserId("")
    setExternalEmail("")
    setSaveForFuture(true)
    setSubject(defaultSubject(reportTitle, billingYear, billingMonth))
    setMessage("")
    setFormats({ pdf: true, csv: true, xlsx: false })
    setSelectedSavedRecipientIds(initiallySelected(savedRecipientRows))
    setCcMe(true)
  }

  const startRecipientMutation = (): boolean => {
    if (recipientMutationRef.current || submitting) return false

    recipientMutationRef.current = true
    setMutatingList(true)
    return true
  }

  const finishRecipientMutation = () => {
    recipientMutationRef.current = false
    setMutatingList(false)
  }

  const handleOpenChange = (next: boolean) => {
    if (!next && (recipientMutationRef.current || submitting)) return

    dialogOpenRef.current = next
    setOpen(next)
    if (next) {
      // Every fresh send starts with every enabled saved recipient checked.
      setSelectedSavedRecipientIds(initiallySelected(savedRecipientRows))
    } else {
      resetComposition()
    }
  }

  const selectedSaved = savedRecipientRows.filter(
    (sub) =>
      sub.enabled &&
      sub.recipient_email &&
      selectedSavedRecipientIds.has(sub.id),
  )

  const selectedRecipientEmails = (() => {
    const seen = new Set<string>()
    return [
      ...selectedSaved.map((sub) => sub.recipient_email ?? ""),
      ...recipients.map((r) => r.email),
    ].filter((email) => {
      const key = recipientKey(email)
      if (!key || seen.has(key)) return false
      seen.add(key)
      return true
    })
  })()

  const findSavedRecipient = (email: string, userId?: number) => {
    const key = recipientKey(email)
    return savedRecipientRows.find(
      (sub) =>
        (userId !== undefined && sub.user_id === userId) ||
        recipientKey(sub.recipient_email) === key,
    )
  }

  const selectExistingSaved = (sub: SavedReportRecipientData): boolean => {
    if (!sub.enabled || !sub.recipient_email) {
      toast.warning(
        sub.enabled
          ? `${sub.recipient_label} no longer has a usable email address.`
          : `${sub.recipient_label} is inactive. An editor or admin must re-enable this recipient.`,
      )
      return false
    }
    setSelectedSavedRecipientIds((prev) => new Set(prev).add(sub.id))
    return true
  }

  const addOneOff = (recipient: Recipient) => {
    setRecipients((prev) => {
      const key = recipientKey(recipient.email)
      if (prev.some((row) => recipientKey(row.email) === key)) return prev
      return [...prev, recipient]
    })
  }

  const createSavedRecipient = async (attrs: {
    user_id?: number
    email?: string
  }): Promise<SavedReportRecipientData> => {
    const res = await csrfFetch(routes.savedReportRecipientsPath(), {
      method: "POST",
      headers: { Accept: "application/json" },
      body: JSON.stringify({
        saved_report_recipient: {
          report_key: reportKey,
          enabled: true,
          ...attrs,
        },
      }),
    })
    const json = (await res
      .json()
      .catch(() => ({}))) as SavedRecipientMutationResponse
    const recipient =
      json.saved_recipient ?? json.saved_report_recipient ?? json.recipient
    if (!res.ok || !json.ok || !recipient) {
      throw new Error(json.error || "Could not save this recipient.")
    }
    return recipient
  }

  const rememberSavedRecipient = (sub: SavedReportRecipientData) => {
    setSavedRecipientRows((prev) => upsertSavedRecipient(prev, sub))
    if (sub.enabled && sub.recipient_email) {
      setSelectedSavedRecipientIds((prev) => new Set(prev).add(sub.id))
    }
  }

  const addOrgUser = async () => {
    if (
      recipientControlsLocked ||
      recipientMutationRef.current ||
      !pickedUserId
    )
      return
    const user = orgUsers.find(
      (candidate) => String(candidate.id) === pickedUserId,
    )
    if (!user) return

    const existing = findSavedRecipient(user.email, user.id)
    if (existing) {
      selectExistingSaved(existing)
      setPickedUserId("")
      return
    }

    if (!(canManageList && saveForFuture)) {
      addOneOff({
        user_id: user.id,
        email: user.email,
        label: `${user.name} (${user.email})`,
      })
      setPickedUserId("")
      return
    }

    if (!startRecipientMutation()) return
    try {
      rememberSavedRecipient(await createSavedRecipient({ user_id: user.id }))
      setPickedUserId("")
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Could not save recipient.",
      )
    } finally {
      finishRecipientMutation()
    }
  }

  const addExternalEmails = async () => {
    if (recipientControlsLocked || recipientMutationRef.current) return

    const emails = Array.from(
      new Map(
        externalEmail
          .split(/[,;]/)
          .map((value) => value.trim())
          .filter(Boolean)
          .map((email) => [recipientKey(email), email]),
      ).values(),
    )
    if (emails.length === 0) return

    const shouldSave = canManageList && saveForFuture
    const failures: { email: string; reason: string }[] = []
    if (shouldSave && !startRecipientMutation()) return

    try {
      // Intentionally sequential. Each address is its own retry-safe JSON CRUD
      // operation, and serial writes avoid transient SQLite tenant lock races.
      for (const email of emails) {
        const existing = findSavedRecipient(email)
        if (existing) {
          if (!selectExistingSaved(existing)) {
            failures.push({
              email,
              reason: "recipient is inactive or unavailable",
            })
          }
          continue
        }

        if (!shouldSave) {
          addOneOff({ email, label: email })
          continue
        }

        try {
          rememberSavedRecipient(await createSavedRecipient({ email }))
        } catch (error) {
          failures.push({
            email,
            reason: error instanceof Error ? error.message : "could not save",
          })
        }
      }
    } finally {
      if (shouldSave) finishRecipientMutation()
    }

    setExternalEmail(failures.map((failure) => failure.email).join(", "))
    if (failures.length > 0) {
      const details = failures
        .slice(0, 3)
        .map((failure) => `${failure.email}: ${failure.reason}`)
        .join("; ")
      toast.warning(
        `${failures.length} ${failures.length === 1 ? "recipient was" : "recipients were"} not added — ${details}`,
      )
    }
  }

  const toggleSavedRecipient = (id: number, checked: boolean) => {
    if (recipientControlsLocked || recipientMutationRef.current) return

    setSelectedSavedRecipientIds((prev) => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }

  const reenableSavedRecipient = async (sub: SavedReportRecipientData) => {
    if (!startRecipientMutation()) return
    try {
      const res = await csrfFetch(routes.savedReportRecipientPath(sub.id), {
        method: "PATCH",
        headers: { Accept: "application/json" },
        body: JSON.stringify({ saved_report_recipient: { enabled: true } }),
      })
      const json = (await res
        .json()
        .catch(() => ({}))) as SavedRecipientMutationResponse
      const recipient =
        json.saved_recipient ?? json.saved_report_recipient ?? json.recipient
      if (!res.ok || !json.ok || !recipient) {
        throw new Error(json.error || "Could not re-enable this recipient.")
      }
      rememberSavedRecipient(recipient)
      toast.success(`${recipient.recipient_label} re-enabled.`)
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Could not re-enable recipient.",
      )
    } finally {
      finishRecipientMutation()
    }
  }

  const deleteSavedRecipient = async (sub: SavedReportRecipientData) => {
    if (recipientControlsLocked || recipientMutationRef.current) return
    if (!window.confirm(`Remove ${sub.recipient_label} from saved recipients?`))
      return
    if (!startRecipientMutation()) return
    try {
      const res = await csrfFetch(routes.savedReportRecipientPath(sub.id), {
        method: "DELETE",
        headers: { Accept: "application/json" },
      })
      const json = (await res.json().catch(() => ({}))) as {
        ok?: boolean
        error?: string
      }
      if (!res.ok || !json.ok) {
        throw new Error(json.error || "Could not remove this recipient.")
      }
      setSavedRecipientRows((prev) => prev.filter((row) => row.id !== sub.id))
      setSelectedSavedRecipientIds((prev) => {
        const next = new Set(prev)
        next.delete(sub.id)
        return next
      })
      toast.success(`${sub.recipient_label} removed.`)
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Could not remove recipient.",
      )
    } finally {
      finishRecipientMutation()
    }
  }

  const removeRecipient = (idx: number) => {
    if (recipientControlsLocked || recipientMutationRef.current) return
    setRecipients((prev) => prev.filter((_, index) => index !== idx))
  }

  const submit = async () => {
    if (recipientControlsLocked || recipientMutationRef.current) return

    const selectedFormats = FORMAT_OPTIONS.filter((format) => formats[format])
    if (selectedFormats.length === 0) {
      toast.error("Pick at least one format.")
      return
    }
    const seen = new Set<string>()
    const sendRecipients: SendRecipient[] = []

    selectedSaved.forEach((sub) => {
      const key = recipientKey(sub.recipient_email)
      if (!key || seen.has(key)) return
      seen.add(key)
      sendRecipients.push({ saved_recipient_id: sub.id })
    })
    recipients.forEach((recipient) => {
      const key = recipientKey(recipient.email)
      if (!key || seen.has(key)) return
      seen.add(key)
      sendRecipients.push({
        user_id: recipient.user_id,
        email: recipient.email,
      })
    })

    if (sendRecipients.length === 0) {
      toast.error("Add at least one recipient.")
      return
    }

    setSubmitting(true)
    try {
      const body: Record<string, unknown> = {
        recipients: sendRecipients,
        formats: selectedFormats,
        cc_me: ccMe,
        subject,
        message,
      }
      if (billingYear && billingMonth) {
        body.billing_year = billingYear
        body.billing_month = billingMonth
      }
      // Forward the on-screen scope (dropping undefined keys) so the emailed
      // attachments match what the operator sees.
      if (scopeParams) {
        for (const [key, value] of Object.entries(scopeParams)) {
          if (value !== undefined) body[key] = value
        }
      }

      const res = await csrfFetch(sendLegacyOrgReportPath(orgSlug, reportKey), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })
      const json = (await res.json().catch(() => ({}))) as {
        ok?: boolean
        enqueued?: number
        rejected?: string[]
        error?: string
      }
      if (!res.ok || !json.ok) {
        toast.error(json.error || "Failed to send.")
        return
      }
      const sent = json.enqueued ?? sendRecipients.length
      toast.success(`Queued ${sent} email${sent === 1 ? "" : "s"}.`)
      if (json.rejected?.length) {
        toast.warning(
          `${json.rejected.length} ${json.rejected.length === 1 ? "recipient was" : "recipients were"} skipped: ${json.rejected.join("; ")}`,
        )
      }
      setOpen(false)
      resetComposition()
      // Refresh the page so send history and any server-normalized list rows land.
      window.setTimeout(() => router.reload({ only: ["send_history"] }), 400)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : String(error))
    } finally {
      setSubmitting(false)
    }
  }

  const toggleFormat = (format: Format, checked: boolean) => {
    setFormats((prev) => ({ ...prev, [format]: checked }))
  }

  const fetchEmailPreview = (): Promise<EmailPreviewData> =>
    requestEmailPreview(emailPreviewLegacyOrgReportPath(orgSlug, reportKey), {
      subject: subject.trim() || undefined,
      message: message.trim() || undefined,
      ...(billingYear && billingMonth
        ? { billing_year: billingYear, billing_month: billingMonth }
        : {}),
    })

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {!controlled && (
        <DialogTrigger asChild>
          <Button variant={triggerVariant} size="sm" className="h-11">
            <Mail className="mr-1 h-4 w-4" />
            {triggerLabel}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="grid max-h-[90vh] max-w-[calc(100%_-_2rem)] grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)] overflow-hidden p-0 **:data-[slot=dialog-close]:flex **:data-[slot=dialog-close]:size-11 **:data-[slot=dialog-close]:items-center **:data-[slot=dialog-close]:justify-center sm:max-w-xl">
        <DialogHeader className="border-b px-5 py-4 sm:px-6">
          <DialogTitle>Send {reportTitle}</DialogTitle>
        </DialogHeader>

        <div className="min-h-0 space-y-4 overflow-y-auto px-5 py-4 sm:px-6">
          {scopeSummary && (
            <p className="bg-muted/50 text-muted-foreground rounded-md border px-3 py-2 text-xs">
              Sending:{" "}
              <span className="text-foreground font-medium">
                {scopeSummary}
              </span>
            </p>
          )}

          <div className="space-y-2">
            <div>
              <Label className="text-xs">Recipients for this send</Label>
              <p className="text-muted-foreground mt-0.5 text-xs">
                Checked recipients get this send. Unchecking only changes this
                send.
              </p>
            </div>

            {savedRecipientRows.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-muted-foreground text-xs">
                  Saved recipients
                </p>
                <div className="divide-border divide-y rounded-md border">
                  {savedRecipientRows.map((sub) => {
                    const unavailable = !sub.enabled || !sub.recipient_email
                    return (
                      <div
                        key={sub.id}
                        className={`flex items-center gap-2 px-3 py-2 text-sm ${unavailable ? "bg-muted/30 opacity-60" : ""}`}
                      >
                        <label
                          htmlFor={`saved-recipient-${sub.id}`}
                          className="flex size-11 shrink-0 cursor-pointer items-center justify-center"
                        >
                          <Checkbox
                            id={`saved-recipient-${sub.id}`}
                            checked={selectedSavedRecipientIds.has(sub.id)}
                            disabled={unavailable || recipientControlsLocked}
                            onCheckedChange={(checked) =>
                              toggleSavedRecipient(sub.id, Boolean(checked))
                            }
                            aria-label={`Send to ${sub.recipient_label}`}
                          />
                        </label>
                        <span
                          className="min-w-0 flex-1 break-all"
                          title={sub.recipient_label}
                        >
                          {sub.recipient_label}
                        </span>
                        <Badge
                          variant="secondary"
                          className="shrink-0 text-[10px]"
                        >
                          Saved
                        </Badge>
                        {!sub.enabled && (
                          <Badge
                            variant="outline"
                            className="shrink-0 text-[10px]"
                          >
                            Inactive
                          </Badge>
                        )}
                        {sub.enabled && !sub.recipient_email && (
                          <Badge
                            variant="outline"
                            className="shrink-0 text-[10px]"
                          >
                            Unavailable
                          </Badge>
                        )}
                        {!sub.enabled &&
                          canReenableSavedRecipients &&
                          sub.recipient_email && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="size-11 shrink-0"
                              disabled={recipientControlsLocked}
                              onClick={() => void reenableSavedRecipient(sub)}
                              aria-label={`Re-enable ${sub.recipient_label}`}
                              title="Re-enable recipient"
                            >
                              <RotateCcw className="size-3.5" />
                            </Button>
                          )}
                        {canManageList && sub.enabled && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="hover:text-destructive size-11 shrink-0"
                            disabled={recipientControlsLocked}
                            onClick={() => void deleteSavedRecipient(sub)}
                            aria-label={`Remove saved recipient ${sub.recipient_label}`}
                            title="Remove saved recipient"
                          >
                            <Trash2 className="size-3.5" />
                          </Button>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {recipients.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-muted-foreground text-xs">
                  Added for this send
                </p>
                <div className="flex flex-wrap gap-1">
                  {recipients.map((recipient, index) => (
                    <Badge
                      key={`${recipient.email}-${index}`}
                      variant="secondary"
                      className="flex max-w-full min-w-0 gap-1 whitespace-normal"
                    >
                      <span
                        className="min-w-0 break-all"
                        title={recipient.label}
                      >
                        {recipient.label}
                      </span>
                      <button
                        type="button"
                        disabled={recipientControlsLocked}
                        onClick={() => removeRecipient(index)}
                        aria-label={`Remove ${recipient.label}`}
                        className="hover:text-destructive -my-3 -mr-3 flex size-11 shrink-0 items-center justify-center disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-3 rounded-md border border-dashed p-3">
              <p className="text-sm font-medium">Add recipients</p>

              {canManageList && (
                <label className="flex min-h-11 cursor-pointer items-start gap-2">
                  <Checkbox
                    checked={saveForFuture}
                    disabled={recipientControlsLocked}
                    onCheckedChange={(checked) =>
                      setSaveForFuture(Boolean(checked))
                    }
                    aria-label="Also save new recipients for future sends"
                  />
                  <span className="text-sm leading-tight">
                    Also save new recipients for future sends
                    <span className="text-muted-foreground mt-0.5 block text-xs">
                      {saveForFuture
                        ? "Saved immediately, even if you cancel this send."
                        : "Added only to this send."}
                    </span>
                  </span>
                </label>
              )}

              <div className="flex items-end gap-2">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="send-report-org-user" className="text-xs">
                    Org user
                  </Label>
                  <Select
                    value={pickedUserId}
                    onValueChange={setPickedUserId}
                    disabled={recipientControlsLocked}
                  >
                    <SelectTrigger id="send-report-org-user" className="h-11">
                      <SelectValue placeholder="Pick a user" />
                    </SelectTrigger>
                    <SelectContent>
                      {orgUsers.map((user) => (
                        <SelectItem
                          key={user.id}
                          value={String(user.id)}
                          className="min-h-11"
                        >
                          {user.name} ({user.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="button"
                  size="sm"
                  className="h-11"
                  variant="outline"
                  onClick={() => void addOrgUser()}
                  disabled={!pickedUserId || recipientControlsLocked}
                >
                  {mutatingList ? (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="mr-1 h-4 w-4" />
                  )}
                  Add
                </Button>
              </div>

              <div className="flex items-end gap-2">
                <div className="flex-1 space-y-1">
                  <Label
                    htmlFor="send-report-external-emails"
                    className="text-xs"
                  >
                    External emails
                  </Label>
                  <Input
                    id="send-report-external-emails"
                    type="text"
                    inputMode="email"
                    disabled={recipientControlsLocked}
                    value={externalEmail}
                    onChange={(event) => setExternalEmail(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault()
                        void addExternalEmails()
                      }
                    }}
                    placeholder="client@example.com, another@example.com"
                    className="h-11"
                  />
                </div>
                <Button
                  type="button"
                  size="sm"
                  className="h-11"
                  variant="outline"
                  onClick={() => void addExternalEmails()}
                  disabled={!externalEmail.trim() || recipientControlsLocked}
                >
                  {mutatingList ? (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="mr-1 h-4 w-4" />
                  )}
                  Add
                </Button>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">CC</Label>
            <label className="flex min-h-11 cursor-pointer items-center gap-2 text-sm">
              <Checkbox
                checked={ccMe}
                disabled={recipientControlsLocked}
                onCheckedChange={(value) => setCcMe(Boolean(value))}
                aria-label="CC me on every email"
              />
              CC me on every email ({currentUser.email})
            </label>
          </div>

          <div className="space-y-1">
            <Label htmlFor="subject" className="text-xs">
              Subject
            </Label>
            <Input
              id="subject"
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              className="h-11"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="message" className="text-xs">
              Message (optional)
            </Label>
            <Textarea
              id="message"
              rows={3}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="Anything you want the recipient to see at the top of the email."
            />
          </div>

          <EmailPreview
            to={selectedRecipientEmails[0] || "Add recipients above"}
            note={
              selectedRecipientEmails.length > 0
                ? `Example: ${selectedRecipientEmails[0]} — ${selectedRecipientEmails.length} separate email${selectedRecipientEmails.length === 1 ? "" : "s"} will be sent with the same report.`
                : undefined
            }
            refreshKey={JSON.stringify([subject, message])}
            fetchPreview={fetchEmailPreview}
          />

          <div className="space-y-1">
            <Label className="text-xs">Attach</Label>
            <div className="flex gap-4">
              {FORMAT_OPTIONS.map((format) => (
                <label
                  key={format}
                  className="flex min-h-11 cursor-pointer items-center gap-1.5 text-sm"
                >
                  <Checkbox
                    checked={formats[format]}
                    onCheckedChange={(checked) =>
                      toggleFormat(format, Boolean(checked))
                    }
                  />
                  {format.toUpperCase()}
                </label>
              ))}
            </div>
          </div>

          <div className="bg-background sticky bottom-0 -mx-5 flex justify-end gap-2 border-t px-5 pt-4 pb-1 sm:-mx-6 sm:px-6">
            <Button
              variant="ghost"
              className="h-11"
              onClick={() => handleOpenChange(false)}
              disabled={submitting || mutatingList}
            >
              Cancel
            </Button>
            <Button
              className="h-11"
              onClick={submit}
              disabled={
                submitting ||
                mutatingList ||
                selectedRecipientEmails.length === 0
              }
            >
              {submitting
                ? "Sending…"
                : `Send ${selectedRecipientEmails.length} email${
                    selectedRecipientEmails.length === 1 ? "" : "s"
                  }`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
